import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const editUserSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email format"),
  role: z.enum(["admin", "staff", "service_bureau", "direct_client"], {
    error: (issue) => issue.input === undefined ? "Role is required" : undefined,
  }),
  companyId: z.number().optional().nullable(),
  tempPassword: z.boolean().default(false),
});

type FormData = z.infer<typeof editUserSchema>;

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  user: any;
  companies: any[];
}

export default function EditUserModal({
  open,
  onClose,
  onSubmit,
  isLoading,
  user,
  companies,
}: EditUserModalProps) {
  const form = useForm<z.input<typeof editUserSchema>, unknown, FormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      role: user?.role || "direct_client",
      companyId: user?.companyId || null,
      tempPassword: user?.tempPassword || false,
    },
  });

  const watchRole = form.watch("role");

  useEffect(() => {
    if (watchRole === "admin" || watchRole === "staff") {
      form.setValue("companyId", null);
    }
  }, [watchRole, form]);

  const handleFormSubmit = (data: FormData) => {
    if ((data.role === "service_bureau" || data.role === "direct_client") && !data.companyId) {
      form.setError("companyId", { message: "Company is required for this role" });
      return;
    }
    onSubmit(data);
  };

  const formatRole = (role: string) => {
    switch (role) {
      case "admin":
        return "Administrator";
      case "staff":
        return "Staff";
      case "service_bureau":
        return "Service Bureau";
      case "direct_client":
        return "Direct Client";
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "staff":
        return "bg-purple-100 text-purple-800";
      case "service_bureau":
        return "bg-blue-100 text-blue-800";
      case "direct_client":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter first name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className={watchRole === "admin" || watchRole === "staff" ? "" : "grid grid-cols-2 gap-4"}>
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-red-100 text-red-800">Admin</Badge>
                            <span>Administrator</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="staff">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-purple-100 text-purple-800">Staff</Badge>
                            <span>Staff</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="service_bureau">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-blue-100 text-blue-800">Service</Badge>
                            <span>Service Bureau</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="direct_client">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-800">Client</Badge>
                            <span>Direct Client</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {watchRole !== "admin" && watchRole !== "staff" && (
                <FormField
                  control={form.control}
                  name="companyId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company</FormLabel>
                      <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString() || ""}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select company" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id.toString()}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {user?.authType === "internal" && (
              <>
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md text-sm">
                  <p className="font-medium text-green-900 dark:text-green-100">User Managed Credentials:</p>
                  <p className="text-green-800 dark:text-green-200 mt-1">
                    The user signs in with their own email and password.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="tempPassword"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Force Password Change</FormLabel>
                        <div className="text-sm text-gray-500">
                          Require user to change password on next login
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}